<template>
  <div class="text-field__wrapper" :class="{ expanded }">
    <div
      class="text-field__container"
      :class="{
        focused: active || modelValue !== '',
        hasIcon: type === 'search' || (type === 'password' && modelValue),
        startIcon: type === 'search',
        endIcon: type === 'password',
        compact,
        search,
        mid,
        short,
        error,
        disabled,
      }"
    >
      <slot name="start" />

      <!-- search icon -->
      <iconify-icon
        icon="fa6-solid:magnifying-glass"
        class="icon search-icon"
        v-show="type === 'search'"
        @click="$emit('enter', modelValue)"
      />

      <div class="input-section">
        <label class="smaller description" v-if="!(hidePlaceholderOnFocus && modelValue)">{{ placeholder }}</label>
        <input
          :disabled="disabled"
          :id="id"
          :placeholder="placeholder"
          :type="showPassword ? 'text' : type"
          :name="name"
          :value="modelValue"
          @input="
            event => {
              $emit('update:modelValue', (event.target as HTMLInputElement).value);
              $emit('change', modelValue);
            }
          "
          @focusin="active = true"
          @focusout="active = false"
          @keyup.enter="$emit('enter', modelValue)"
          @blur="$emit('blur', $event)"
          :maxlength="max"
          :style="{ textOverflow: showEllipsis ? 'ellipsis' : 'clip' }"
        />
      </div>

      <div class="icon__wrapper end" v-if="type === 'password'">
        <iconify-icon icon="fa-solid:eye" class="icon" v-if="showPassword" @click="showPassword = false" />
        <iconify-icon icon="fa-solid:eye-slash" class="icon" v-else @click="showPassword = true" />
      </div>

      <slot name="end" />
    </div>

    <!-- Error message -->
    <transition name="scale-fade">
      <div v-if="error" class="smaller communication-danger text-field__error">
        <span>
          {{ error }}
        </span>
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';

  export interface Props {
    type?: 'text' | 'email' | 'search' | 'date' | 'date-time' | 'number' | 'password';
    name?: string;
    error?: string | null;
    placeholder?: string; // for the floating text within the input
    id?: string;
    compact?: boolean;
    short?: boolean;
    mid?: boolean;
    search?: boolean;
    expanded?: boolean;
    modelValue?: string | number;
    max?: number;
    disabled?: boolean;
    showEllipsis?: boolean;
    hidePlaceholderOnFocus?: boolean;
  }
  withDefaults(defineProps<Props>(), {
    type: 'text',
    compact: false,
    modelValue: '',
    error: null,
  });

  const active = ref(false);
  const showPassword = ref(false);

  defineEmits(['update:modelValue', 'blur', 'change', 'enter']);
</script>

<style scoped lang="scss">
  @import '@/assets/styles/variables.scss';

  .text-field__wrapper {
    width: 300px;
    &.expanded {
      width: 100%;
    }
  }

  .text-field__error {
    padding: 0.5rem 1.5rem;
    width: 100%;
  }

  .text-field__container {
    flex: 1;
    width: 100%;
    height: 3.5rem;
    display: flex;
    align-items: center;
    font-size: 0.825rem;
    background-color: $white;
    border: 1px solid $input-border;
    border-radius: 8px;

    &:focus-within {
      border-color: $var-primary-light;
    }
    &.disabled {
      background-color: $bg-dark;
      cursor: not-allowed;
      &:focus-within {
        border-color: $input-border;
      }
    }
    &.error {
      border-color: $communication-danger;
    }

    * {
      transition: all 0.2s;
    }

    .input-section {
      flex: 1;
      height: 100%;
      position: relative;
    }

    label {
      padding: 1rem 1.5rem;
      position: absolute;
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      z-index: 1;
      color: $input-placeholder;
      pointer-events: none;
      font-size: 0.875rem;
    }

    input {
      caret-color: $var-primary-main;
      width: 100%;
      height: 100%;
      border: none;
      outline: none;
      padding: 1rem 1.5rem;
      font-size: 0.875rem;
      background-color: transparent;

      &:autofill {
        background-color: #e8f0fe;
        background-image: none !important;
        color: -internal-light-dark(black, white) !important;
      }
      &::placeholder {
        opacity: 0;
        color: $input-placeholder;
      }
    }

    .icon {
      color: $neutral-gray;
      margin-inline: 0.5rem;
      height: 1.25rem;
    }
    .search-icon {
      cursor: pointer;
    }

    &.focused {
      label {
        opacity: 0.6;
        height: 1.5rem;
        padding: 0.375rem 1.5rem 0 1.5rem;
        font-size: 0.75rem;
        inset: 0 0 auto 0;
      }
      input {
        padding-top: 1.5625rem;
      }
    }

    &.compact {
      height: 2.5rem;

      input {
        padding: 0.5rem 0.75rem;
        font-size: 0.75rem;

        &::placeholder {
          opacity: 1;
        }
      }

      &.startIcon {
        input {
          padding-inline-start: 0;
        }
      }

      .icon {
        height: 1rem;
      }

      label {
        display: none;
      }
    }
    &.short {
      height: 24px;
    }
    &.mid {
      height: 32px;
    }
  }
</style>
